import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Select, Space, Spin, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { TransportRouteSummaryDto } from '../../../dto/transport';
import { listTransportRoutesForSelection } from '../../transports/api/transportRoutesApi';
import { formatDate } from '../../../utils/dateUtils';
import { buildTransportRouteLabel } from '../transportAddressUtils';

const { Text } = Typography;
const TERMINAL_STATUSES = new Set(['DELIVERED', 'CANCELLED']);

interface OrderTransportLinkModalProps {
  open: boolean;
  orderId: string;
  linkedTransportIds: string[];
  onClose: () => void;
}

export function getAvailableTransportsForOrderLink(
  transports: TransportRouteSummaryDto[],
  linkedTransportIds: string[],
): TransportRouteSummaryDto[] {
  const linkedIds = new Set(linkedTransportIds);
  return transports.filter((transport) => (
    !linkedIds.has(transport.id) && !TERMINAL_STATUSES.has(transport.status)
  ));
}

function buildSelectionLabel(transport: TransportRouteSummaryDto): string {
  const route = buildTransportRouteLabel(
    transport.loadingAddresses,
    transport.deliveryAddresses,
    transport.originCity,
    transport.destinationCity,
  );
  const date = formatDate(transport.loadingDate);

  return [
    transport.routeNumber,
    route.label,
    date,
    transport.transportCompany,
  ].filter((value) => value && value !== '-').join(' · ');
}

const OrderTransportLinkModal: React.FC<OrderTransportLinkModalProps> = ({
  open,
  orderId,
  linkedTransportIds,
  onClose,
}) => {
  const { t } = useTranslation(['transport', 'common']);
  const navigate = useNavigate();
  const [transports, setTransports] = useState<TransportRouteSummaryDto[]>([]);
  const [selectedTransportId, setSelectedTransportId] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let active = true;
    setLoading(true);
    setLoadFailed(false);
    setSelectedTransportId(undefined);

    void listTransportRoutesForSelection()
      .then((routes) => {
        if (active) setTransports(routes);
      })
      .catch(() => {
        if (active) setLoadFailed(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open]);

  const availableTransports = useMemo(
    () => getAvailableTransportsForOrderLink(transports, linkedTransportIds),
    [linkedTransportIds, transports],
  );

  const openNewTransport = () => {
    onClose();
    navigate(`/app/transports/new?orderId=${encodeURIComponent(orderId)}`);
  };

  const openExistingTransport = () => {
    if (!selectedTransportId) return;
    onClose();
    navigate(
      `/app/transports/${encodeURIComponent(selectedTransportId)}/edit?orderId=${encodeURIComponent(orderId)}`,
    );
  };

  return (
    <Modal
      title={t('transport:orderTransports.linkDialogTitle')}
      open={open}
      onCancel={onClose}
      footer={(
        <Space wrap>
          <Button onClick={onClose}>{t('common:cancel')}</Button>
          <Button icon={<PlusOutlined />} onClick={openNewTransport}>
            {t('transport:orderTransports.createNew')}
          </Button>
          <Button
            type="primary"
            disabled={!selectedTransportId}
            onClick={openExistingTransport}
          >
            {t('transport:orderTransports.editAndLink')}
          </Button>
        </Space>
      )}
      destroyOnHidden
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text>{t('transport:orderTransports.linkDialogDescription')}</Text>
        {loadFailed ? (
          <Alert
            type="error"
            showIcon
            message={t('transport:orderTransports.loadExistingError')}
          />
        ) : null}
        <Select
          showSearch
          allowClear
          value={selectedTransportId}
          onChange={setSelectedTransportId}
          placeholder={t('transport:orderTransports.selectExisting')}
          optionFilterProp="label"
          notFoundContent={loading
            ? <Spin size="small" />
            : t('transport:orderTransports.noAvailableExisting')}
          options={availableTransports.map((transport) => ({
            value: transport.id,
            label: buildSelectionLabel(transport),
          }))}
          style={{ width: '100%' }}
          loading={loading}
        />
        <Alert
          type="info"
          showIcon
          message={t('transport:orderTransports.linkDialogHint')}
        />
      </Space>
    </Modal>
  );
};

export default OrderTransportLinkModal;
